import { geoFindMe } from "../../utils/Location";
import Dialog from "@mui/material/Dialog";
import { SelectLocationProps } from "./types"
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import { Status, Wrapper } from "@googlemaps/react-wrapper";
import { makeStyles } from '@mui/styles';
import { Theme, createStyles } from '@mui/material/styles';
import { useEffect, useRef, useState } from "react";
import DialogTitle from "@mui/material/DialogTitle";
import { Typography } from "@mui/material";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
    APIProvider,
    ControlPosition,
    MapControl,
    AdvancedMarker,
    Map,
    useMap,
    useMapsLibrary,
    useAdvancedMarkerRef,
    AdvancedMarkerRef
} from '@vis.gl/react-google-maps';
import './styles.css';

const mapDivName = "map";

interface ComponentStyles {
    map: string;
    dialogPaper: string;
    autocompleteContainer: string;
    autocompleteControl: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        map: {
            height: "60vh",
            width: "100%"
        },
        dialogPaper: {
            height: "90vh",
            width: "90vw"
        },
        autocompleteContainer: {
            width: 300,
            '& input': {
              width: '100%',
              height: 40,
              padding: '0 0 0 12',
              fontSize: 18,
            },
            '& .custom-list': {
              width: '100%',
              listStyle: 'none',
              padding: 0,
              margin: 0,
            },
            '& .custom-list-item': {
              padding: 8,
              '&:hover': {
                background: 'lightgrey',
                cursor: 'pointer',
              },
            },
          },
          autocompleteControl: {
            boxSizing: 'border-box',
            margin: '24px 10px 24px 24px',
            background: '#fff',
          }
    }),
);

const SelectLocationModal: React.FunctionComponent<SelectLocationProps> = (props) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);
    const [userLocation, setUserLocation] = useState<GeolocationPosition | null>(null);
    const [imgX, setImgX] = useState(0); // used for user location button
    const [markerRef, marker] = useAdvancedMarkerRef();
    const classes = useStyles() as ComponentStyles;
    

    interface PlaceAutocompleteProps {
        onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
    }
      
    const PlaceAutocomplete = ({ onPlaceSelect }: PlaceAutocompleteProps) => {
        const [placeAutocomplete, setPlaceAutocomplete] =
            useState<google.maps.places.Autocomplete | null>(null);
        const inputRef = useRef<HTMLInputElement>(null);
        const places = useMapsLibrary('places');
    
        useEffect(() => {
            if (!places || !inputRef.current) return;
    
            const options = {
                fields: ['geometry', 'name', 'formatted_address']
            };
    
            setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
        }, [places]);
    
        useEffect(() => {
            if (!placeAutocomplete) return;
    
            placeAutocomplete.addListener('place_changed', () => {
                setImgX(0);
                setUserLocation(null);
                onPlaceSelect(placeAutocomplete.getPlace());
            });
        }, [onPlaceSelect, placeAutocomplete]);
    
        return (
            <div className={classes.autocompleteContainer}>
                <input ref={inputRef} />
            </div>
        );
    };
    
    interface MapHandlerProps {
        place: google.maps.places.PlaceResult | null;
        marker: google.maps.marker.AdvancedMarkerElement | null;
    }
      
    const MapHandler = ({ place, marker }: MapHandlerProps) => {
        const map = useMap();
    
        const userLocationHandler = () => {
            if (!map || !userLocation || !marker) return false;

            const coords = userLocation.coords;
            var latlng = new google.maps.LatLng(coords.latitude, coords.longitude);
            marker.position = latlng;
            map.setCenter(latlng);

            return true;
        };

        const placesHandler = () => {
            if (!map || !place || !place.geometry || !marker) return false;
    
            if (place.geometry.viewport) {
                map.fitBounds(place.geometry.viewport);
            }
            marker.position = place.geometry.location;
            
            return true;
        }

        useEffect(() => {
            userLocationHandler() || placesHandler()
        }, [map, place, marker, userLocation]);
    
        return null;
    };
    
    const UserLocationButton = () => {
        const style = {
            margin: '5px',
            width: '18px', 
            height: '18px' ,
            backgroundImage: "url(https://maps.gstatic.com/tactile/mylocation/mylocation-sprite-1x.png)",
            backgroundSize: '180px 18px',
            backgroundPosition: `${imgX}px 0px`,
            backgroundRepeat: 'no-repeat'
        };

        const clickHandler = async () => {
            var animationInterval = setInterval(function(){
                if(imgX == -18) setImgX(0);
                else setImgX(-18);
            }, 500);
            await geoFindMe((position: GeolocationPosition) => {
                clearInterval(animationInterval);
                if (position) {
                    setImgX(-144);
                    setUserLocation(position);    
                    setSelectedPlace(null);
                } else {
                    setImgX(0);
                }
            });
        };

        return (
            <div>
                <button
                onClick={clickHandler}
                title='Your Location'
                style={{
                    backgroundColor: '#fff',
                    border: 'none',
                    width: '28px',
                    height: '28px',
                    borderRadius: '2px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    marginRight: '10px',
                    padding: '0px'
                }}
                >
                <div
                    id="you_location_img"
                    style={style}
                />
                </button>
            </div>
        );
    };

    const getReturnValue = () => {
        if (selectedPlace?.geometry?.location) {
            return new google.maps.LatLng(selectedPlace?.geometry?.location.lat(), selectedPlace?.geometry?.location.lng());
        } else if (userLocation) {
            return new google.maps.LatLng(userLocation.coords.latitude, userLocation.coords.longitude);
        } else {
            return null;
        }
    }

    return (
        <Dialog open={props.open} classes={{ paper: classes.dialogPaper }} fullScreen={fullScreen}>
            <DialogTitle>Claim Kandi - Geotagging</DialogTitle>
            <DialogContent>
                <Typography>
                    Here you can enter where you recieved your Kandi so we can add the geolocation to the history.
                </Typography>
                <br />
                <Typography>
                    Search for a custom location or select your current location below.
                </Typography>
                <br />
                <Typography>
                    Selecting your current location will prompt you for permissions if you haven't already granted them.
                </Typography>
                <p id="status"></p>
                <div id={mapDivName} className={classes.map}>
                    <APIProvider apiKey={`${process.env.REACT_APP_MAPS_API_KEY}`}>
                        <Map
                            mapId={'bf51a910020fa25a'}
                            defaultZoom={11}
                            defaultCenter={{ lat: 47.6131229, lng: -122.4121035 }}
                            gestureHandling={'greedy'}
                            disableDefaultUI={true}
                        >
                            <AdvancedMarker ref={markerRef} position={null} />
                        </Map>
                        <MapControl position={ControlPosition.TOP}>
                            <div style={{display:"flex", flexDirection:"row", alignItems:"center", justifyContent:"flex-start"}}>
                                <div className={classes.autocompleteControl}>
                                    <PlaceAutocomplete onPlaceSelect={setSelectedPlace} />
                                </div>
                                <UserLocationButton />
                            </div>
                        </MapControl>
                        <MapHandler place={selectedPlace} marker={marker} />
                    </APIProvider>
                </div>
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={props.onClose}>
                    Cancel
                </Button>
                <Button color="primary" onClick={()=> {props.onConfirm(getReturnValue())}} disabled={getReturnValue() ? false : true}>
                    Confirm
                </Button>
             </DialogActions>
        </Dialog>
    );
}

export default SelectLocationModal;